import React, { useState, useEffect } from 'react';
import { Heart } from 'lucide-react';
import { useWishlist } from '../../context/WishlistContext';
import { useUserAuth } from '../../context/UserAuthContext';
import './ProductCard.css';
import { CircularProgress } from '@mui/material';
import WhatsAppButton from './WhatsAppButton';
import ProductStructuredData from './ProductStructuredData';

const ProductCard = ({ product, onRemoveFromWishlist }) => {
  const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlist();
  const { isAuthenticated } = useUserAuth();
  const [isAnimating, setIsAnimating] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [wishlistLoading, setWishlistLoading] = useState(false);

  // Ensure product has all required properties
  const safeProduct = {
    id: product?.id || product?.product_id || 0,
    name: product?.name || 'Product Name',
    price: product?.price || 'Request for price',
    image: product?.image || '/media/placeholder.jpeg',
    category: product?.category || 'uncategorized',
    ...product
  };

  // Check if product is in wishlist
  useEffect(() => {
    const checkWishlistStatus = async () => {
      try {
        const inWishlist = await isInWishlist(safeProduct.id || safeProduct.product_id);
        setIsWishlisted(inWishlist);
      } catch (error) {
        console.error('Error checking wishlist status:', error);
        setIsWishlisted(false);
      }
    };

    checkWishlistStatus();
  }, [isAuthenticated, safeProduct.id, safeProduct.product_id, isInWishlist]);

  const handleWishlistClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isAuthenticated) {
      // Redirect to login if not authenticated
      window.location.href = '/auth';
      return;
    }
    
    setIsAnimating(true);
    setWishlistLoading(true);
    
    try {
      if (isWishlisted) {
        // If onRemoveFromWishlist prop is provided, use it (for WishlistPage)
        if (onRemoveFromWishlist) {
          await onRemoveFromWishlist();
        } else {
          await removeFromWishlist(safeProduct.id || safeProduct.product_id, safeProduct.name);
        }
      } else {
        // Ensure product has a valid ID before adding to wishlist
        const productToAdd = {
          ...safeProduct,
          id: safeProduct.id || safeProduct.product_id || `product_${Date.now()}`
        };
        
        // Log the product being added to help with debugging
        console.log('Adding product to wishlist:', productToAdd);
        
        // First try to add the product to the database if needed
        try {
          // This would be a call to a product service in a real implementation
          console.log('Ensuring product exists in database before adding to wishlist');
        } catch (error) {
          console.error('Error ensuring product exists in database:', error);
        }
        
        await addToWishlist(productToAdd);
      }
      setIsWishlisted(!isWishlisted);
    } catch (error) {
      console.error('Error updating wishlist:', error);
    } finally {
      setWishlistLoading(false);
      setTimeout(() => setIsAnimating(false), 500);
    }
  };

  const handleImageError = (e) => {
    console.log('Image load error for:', safeProduct.name, 'URL:', e.target.src);
    
    // Try different image path variations
    const originalSrc = e.target.src;
    
    // If the image URL is relative, try with domain
    if (originalSrc.startsWith('/')) {
      const withDomain = `https://nmdiamond.in${originalSrc}`;
      console.log('Trying with domain:', withDomain);
      e.target.src = withDomain;
      return;
    }
    
    // If the image URL has domain but fails, try without domain
    if (originalSrc.includes('nmdiamond.in') && originalSrc.includes('/media/')) {
      const withoutDomain = originalSrc.substring(originalSrc.indexOf('/media/'));
      console.log('Trying without domain:', withoutDomain);
      e.target.src = withoutDomain;
      return;
    }
    
    // If all else fails, use placeholder
    if (e.target.src !== '/media/placeholder.jpeg') {
      console.log('Using placeholder image');
      e.target.src = '/media/placeholder.jpeg';
      setImageError(true);
    }
    
    setImageLoaded(true);
  };
  
  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  // Format price if it's a number
  const formattedPrice = typeof safeProduct.price === 'number' 
    ? `₹${safeProduct.price.toLocaleString()}` 
    : safeProduct.price;
  
  // Use a placeholder image if the product image is missing or invalid
  const imageSrc = imageError ? '/media/placeholder.jpeg' : safeProduct.image;

  // Create product object for WhatsApp contact
  const whatsappProduct = {
    name: safeProduct.name,
    price: formattedPrice,
    description: safeProduct.description || `${safeProduct.name} from our collection`,
    image: imageSrc,
    specifications: safeProduct.specifications || {}
  };

  return (
    <div className="product-card">
      <ProductStructuredData product={safeProduct} />
      
      <div className="product-image">
        {!imageLoaded && (
          <div className="image-loading">
            <CircularProgress size={30} style={{ color: 'var(--neon-blue)' }} />
          </div>
        )}
        <img 
          src={imageSrc} 
          alt={safeProduct.name} 
          onError={handleImageError}
          onLoad={handleImageLoad}
          style={{ 
            display: imageLoaded ? 'block' : 'none',
            objectFit: 'cover'
          }}
        />
        <button 
          className={`wishlist-button ${isWishlisted ? 'active' : ''} ${isAnimating ? 'animate-pulse' : ''}`}
          onClick={handleWishlistClick}
          disabled={wishlistLoading}
          aria-label={isWishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
        >
          {wishlistLoading ? (
            <CircularProgress size={16} style={{ color: 'white' }} />
          ) : (
            <Heart className="heart-icon" fill={isWishlisted ? 'currentColor' : 'none'} />
          )}
        </button>
        <div className="product-overlay">
          <WhatsAppButton 
            product={whatsappProduct} 
            buttonText="Contact Us" 
            size="small"
            variant="contained"
            color="primary"
            buttonProps={{
              className: "view-details"
            }}
          />
        </div>
      </div>
      <div className="product-info">
        <h3 className="product-name">{safeProduct.name}</h3>
        <p className="product-price">{formattedPrice}</p>
        {safeProduct.category && (
          <p className="product-category">
            {safeProduct.category.charAt(0).toUpperCase() + safeProduct.category.slice(1)}
          </p>
        )}
      </div>
      <div className="product-glow"></div>
    </div>
  );
};

export default ProductCard; 